import React, { useEffect } from 'react';
import { Helmet } from 'react-helmet';
import Header from '../components/ui/Header';
import Footer from './you-tube-video-downloader-landing-page/components/Footer';
import SplashCursor from './you-tube-video-downloader-landing-page/components/SplashCursor';
import Icon from '../components/AppIcon';

const FeaturesPage = () => {
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  const mainFeatures = [
    {
      icon: 'Download',
      title: 'High-Quality Downloads',
      description: 'Download videos in up to 4K resolution with crystal-clear audio quality.',
      details: ['4K, 1080p, 720p, 480p, 360p support', 'Multiple audio formats', 'Lossless quality preservation']
    },
    {
      icon: 'Zap',
      title: 'Lightning Fast',
      description: 'Advanced download acceleration technology for the fastest possible speeds.',
      details: ['Multi-threaded downloading', 'Optimized servers', 'Resume interrupted downloads']
    },
    {
      icon: 'Shield',
      title: 'Secure & Private',
      description: 'Your privacy is protected with SSL encryption and no data storage.',
      details: ['SSL/TLS encryption', 'No personal data stored', 'Anonymous downloads']
    },
    {
      icon: 'Smartphone',
      title: 'Mobile Friendly',
      description: 'Works perfectly on all devices with responsive design.',
      details: ['iOS & Android support', 'Touch-optimized interface', 'Progressive Web App']
    },
    {
      icon: 'Globe',
      title: 'Universal Access',
      description: 'No registration required, completely free to use worldwide.',
      details: ['No account needed', 'Unlimited downloads', 'Global accessibility']
    },
    {
      icon: 'Palette',
      title: 'Beautiful Interface',
      description: 'Stunning glassmorphism design with smooth animations.',
      details: ['Modern UI/UX', 'Dark theme optimized', 'Fluid interactions']
    }
  ];

  const additionalFeatures = [
    'Batch download support',
    'Playlist download capability',
    'Audio-only extraction (MP3)',
    'Custom quality selection',
    'Download history tracking',
    'Browser extension support',
    'API access for developers',
    'Regular updates and improvements'
  ];

  const qualityComparison = [
    { quality: '4K (2160p)', fileSize: '~2GB', bitrate: '40-50 Mbps', useCase: 'Professional editing' },
    { quality: '1080p HD', fileSize: '~800MB', bitrate: '8-12 Mbps', useCase: 'High-quality viewing' },
    { quality: '720p HD', fileSize: '~400MB', bitrate: '5-8 Mbps', useCase: 'Standard viewing' },
    { quality: '480p SD', fileSize: '~200MB', bitrate: '2-3 Mbps', useCase: 'Mobile viewing' },
    { quality: '360p', fileSize: '~100MB', bitrate: '1-2 Mbps', useCase: 'Low bandwidth' }
  ];

  return (
    <>
      <Helmet>
        <title>Features - Zee Downloader | YouTube Video Downloader</title>
        <meta name="description" content="Discover all the powerful features of Zee Downloader. High-quality downloads, fast speeds, secure processing, and more." />
      </Helmet>
      
      <div className="min-h-screen bg-background relative overflow-hidden">
        <SplashCursor />
        
        <div className="fixed inset-0 bg-background/20 backdrop-blur-sm" style={{ zIndex: 2 }} />
        
        <div className="relative" style={{ zIndex: 50 }}>
          <Header />
        </div>

        <main className="relative pt-20 pb-8" style={{ zIndex: 10 }}>
          <div className="glass-container">
            {/* Page Header */}
            <div className="text-center py-12 mb-8">
              <div className="glass-card p-8 max-w-3xl mx-auto">
                <h1 className="text-fluid-4xl font-bold text-foreground mb-4">
                  Powerful Features
                </h1>
                <p className="text-fluid-lg text-muted-foreground">
                  Discover why millions of users choose Zee Downloader for their YouTube video downloading needs.
                </p>
              </div>
            </div>

            {/* Main Features Grid */}
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8 mb-16">
              {mainFeatures?.map((feature, index) => (
                <div key={index} className="glass-card p-8 micro-lift glass-hover h-full">
                  <div className="glass-card p-4 w-fit mb-6">
                    <Icon name={feature?.icon} size={32} className="text-accent" />
                  </div>
                  <h3 className="text-fluid-xl font-semibold text-foreground mb-4">
                    {feature?.title}
                  </h3>
                  <p className="text-muted-foreground mb-4 leading-relaxed">
                    {feature?.description}
                  </p>
                  <ul className="space-y-2">
                    {feature?.details?.map((detail, idx) => (
                      <li key={idx} className="flex items-center text-sm text-muted-foreground">
                        <Icon name="Check" size={16} className="text-accent mr-2 flex-shrink-0" />
                        {detail}
                      </li>
                    ))}
                  </ul>
                </div>
              ))}
            </div>

            {/* Quality Comparison */}
            <div className="mb-16">
              <div className="text-center mb-8">
                <h2 className="text-fluid-3xl font-bold text-foreground mb-4">
                  Quality Comparison
                </h2>
                <p className="text-fluid-base text-muted-foreground max-w-2xl mx-auto">
                  Choose the perfect quality for your needs with our comprehensive format support.
                </p>
              </div>
              
              <div className="glass-card p-8">
                <div className="overflow-x-auto">
                  <table className="w-full">
                    <thead>
                      <tr className="border-b border-border/20">
                        <th className="text-left py-4 px-4 text-foreground font-semibold">Quality</th>
                        <th className="text-left py-4 px-4 text-foreground font-semibold">File Size</th>
                        <th className="text-left py-4 px-4 text-foreground font-semibold">Bitrate</th>
                        <th className="text-left py-4 px-4 text-foreground font-semibold">Best For</th>
                      </tr>
                    </thead>
                    <tbody>
                      {qualityComparison?.map((item, index) => (
                        <tr key={index} className="border-b border-border/10 last:border-0">
                          <td className="py-4 px-4 font-medium text-foreground">{item?.quality}</td>
                          <td className="py-4 px-4 text-muted-foreground">{item?.fileSize}</td>
                          <td className="py-4 px-4 text-muted-foreground">{item?.bitrate}</td>
                          <td className="py-4 px-4 text-muted-foreground">{item?.useCase}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            </div>

            {/* Additional Features */}
            <div className="mb-16">
              <div className="text-center mb-8">
                <h2 className="text-fluid-3xl font-bold text-foreground mb-4">
                  Additional Features
                </h2>
                <p className="text-fluid-base text-muted-foreground max-w-2xl mx-auto">
                  Even more reasons to choose Zee Downloader for all your video downloading needs.
                </p>
              </div>
              
              <div className="glass-card p-8">
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
                  {additionalFeatures?.map((feature, index) => (
                    <div key={index} className="glass-panel p-4 micro-lift glass-hover text-center">
                      <Icon name="Star" size={20} className="text-accent mb-2 mx-auto" />
                      <span className="text-sm text-foreground font-medium">{feature}</span>
                    </div>
                  ))}
                </div>
              </div>
            </div>

            {/* Performance Stats */}
            <div className="mb-16">
              <div className="text-center mb-8">
                <h2 className="text-fluid-3xl font-bold text-foreground mb-4">
                  Performance Statistics
                </h2>
              </div>
              
              <div className="grid grid-cols-1 md:grid-cols-4 gap-6">
                {[
                  { number: '99.9%', label: 'Success Rate', icon: 'Target' },
                  { number: '<10s', label: 'Average Speed', icon: 'Clock' },
                  { number: '100+', label: 'Formats Supported', icon: 'FileType' },
                  { number: '24/7', label: 'Availability', icon: 'Globe' }
                ]?.map((stat, index) => (
                  <div key={index} className="glass-card p-6 text-center micro-lift glass-hover">
                    <div className="glass-card p-3 w-fit mx-auto mb-4">
                      <Icon name={stat?.icon} size={24} className="text-accent" />
                    </div>
                    <div className="text-fluid-2xl font-bold text-foreground mb-1">
                      {stat?.number}
                    </div>
                    <div className="text-sm text-muted-foreground">
                      {stat?.label}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </main>

        <div className="relative" style={{ zIndex: 10 }}>
          <Footer />
        </div>
      </div>
    </>
  );
};

export default FeaturesPage;